/**
 * SpeechBubble — a soft rounded overhead chat bubble with a little downward tail,
 * a spoken line inside, drawn in the cozy palette. Pops above a character's HEAD when
 * they speak, so the tavern reads as alive and chatty (CLAUDE.md: residents "chatting
 * in bubbles", the signature living-hearth feel) — shown IN ADDITION to the scrolling
 * chat-panel line, never instead of it.
 *
 * Transient by design: it eases in, dwells (~5s), then fades out and self-destroys, so
 * bubbles never accrete into clutter. A few concurrent bubbles (nearby regulars only)
 * keep the room cozy, not spammy — the caller (TownScene) pools/limits them.
 *
 * Cheap + self-contained: one Graphics for the rounded body + tail, one wrapped Text.
 * Origin is the bubble's BOTTOM-CENTRE (the tail tip), so the caller positions it at the
 * point just above a head and the body floats up from there — exactly the overhead read.
 * It owns its dwell timer and cleans up on scene shutdown; reduced-motion shows/hides
 * instantly. Touch-safe: purely decorative, never steals taps from the walker beneath.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './theme.js';

export interface SpeechBubbleOptions {
  /** The spoken line shown inside the bubble. */
  readonly text: string;
  /** Max bubble width (px); the text wraps within. Defaults to a cozy 200. */
  readonly maxWidth?: number;
  /** ms on screen before it fades out and self-destroys. Defaults to ~5s. */
  readonly duration?: number;
  /** Skip the in/out tween (calm setting); show + hide instantly. */
  readonly reducedMotion?: boolean;
  /** A warm accent for the player's own bubbles vs. residents' (border tint). */
  readonly accent?: number;
}

/** Default dwell before fade-out (CLAUDE.md task: transient ~5 seconds). */
const DEFAULT_DURATION = 5000;
/** Pixels the body floats above the anchor (the tail's height). */
const TAIL_H = 9;
/** Half-width of the tail's base. */
const TAIL_HALF = 7;
/** Inner padding around the text inside the bubble. */
const PAD_X = SPACING.sm + 2;
const PAD_Y = SPACING.xs + 2;

export class SpeechBubble extends Phaser.GameObjects.Container {
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private readonly reduced: boolean;
  private dwell?: Phaser.Time.TimerEvent;
  private dismissing = false;

  /** Full rendered height (body + tail), so the caller can offset above a head. */
  readonly bubbleHeight: number;
  /** Full rendered width, for the caller's overlap thinning if it wants it. */
  readonly bubbleWidth: number;

  constructor(scene: Phaser.Scene, x: number, y: number, options: SpeechBubbleOptions) {
    super(scene, x, y);
    this.reduced = options.reducedMotion ?? false;
    const accent = options.accent ?? COLORS.accent;
    const maxW = options.maxWidth ?? 200;

    // The line, wrapped to the cozy max width. Measured first so the body fits it.
    this.label = scene.add
      .text(0, 0, options.text, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxText,
        align: 'center',
        wordWrap: { width: maxW - PAD_X * 2 },
        lineSpacing: 2,
      })
      .setOrigin(0.5);

    const w = Math.ceil(this.label.width) + PAD_X * 2;
    const h = Math.ceil(this.label.height) + PAD_Y * 2;
    this.bubbleWidth = w;
    this.bubbleHeight = h + TAIL_H;

    // The body's vertical centre sits the tail-height above the anchor (bottom-centre
    // origin), so the whole bubble floats just over the head we were positioned at.
    const bodyCy = -TAIL_H - h / 2;
    this.label.setPosition(0, bodyCy);

    this.bg = scene.add.graphics();
    this.drawBubble(w, h, bodyCy, accent);

    this.add([this.bg, this.label]);
    // Decorative only — explicitly NON-interactive so taps fall through to the walker.
    this.setDepth(300);

    scene.add.existing(this);

    if (!this.reduced) {
      this.setAlpha(0);
      this.setScale(0.92);
      scene.tweens.add({
        targets: this,
        alpha: 1,
        scale: 1,
        duration: 160,
        ease: 'Back.easeOut',
      });
    }

    const duration = options.duration ?? DEFAULT_DURATION;
    this.dwell = scene.time.delayedCall(duration, () => this.dismiss());
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  /** Re-aim the bubble at a fresh anchor point (the speaker walks while it dwells). */
  follow(x: number, y: number): void {
    if (!this.dismissing) this.setPosition(x, y);
  }

  /** Fade out + self-destroy (idempotent). The caller may also just let it time out. */
  dismiss(): void {
    if (this.dismissing) return;
    this.dismissing = true;
    this.dwell?.remove(false);
    this.dwell = undefined;
    if (this.reduced || !this.scene) {
      this.destroy();
      return;
    }
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: 0.94,
      duration: 200,
      ease: 'Sine.easeIn',
      onComplete: () => this.destroy(),
    });
  }

  override destroy(fromScene?: boolean): void {
    this.dwell?.remove(false);
    this.dwell = undefined;
    super.destroy(fromScene);
  }

  /** Draw the rounded cream body + the downward tail, with a soft accent border. */
  private drawBubble(w: number, h: number, bodyCy: number, accent: number): void {
    const g = this.bg;
    const r = Math.min(RADIUS.md, h / 2);
    const top = bodyCy - h / 2;
    const left = -w / 2;
    g.clear();

    // Soft drop shadow for a hint of lift over the busy room.
    g.fillStyle(COLORS.scrim, 0.22);
    g.fillRoundedRect(left + 1, top + 3, w, h, r);

    // Cream paper fill (the classic message-box paper, scaled down to a bubble).
    g.fillStyle(COLORS.boxFill, 1);
    g.fillRoundedRect(left, top, w, h, r);

    // The downward tail: a small triangle from the body's bottom edge to the anchor.
    g.fillStyle(COLORS.boxFill, 1);
    g.fillTriangle(-TAIL_HALF, -TAIL_H, TAIL_HALF, -TAIL_H, 0, 0);

    // The crisp dark G/S frame so the bubble reads as a little message box, plus a
    // thin accent line inside it (warm for the player, blue for residents) for warmth.
    g.lineStyle(2, COLORS.boxBorder, 1);
    g.strokeRoundedRect(left, top, w, h, r);
    g.lineStyle(1, accent, 0.85);
    g.strokeRoundedRect(left + 2, top + 2, w - 4, h - 4, Math.max(2, r - 2));
  }
}
